import { TrendingDown } from "lucide-react";

export default function TCOComparisonSection() {
  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" data-testid="tco-comparison-section">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/20 text-emerald-400 text-sm font-semibold mb-6">
            <TrendingDown className="w-4 h-4" />
            Cost Savings
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-4">
            3-Year TCO Comparison: 1,000 Users
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Modular License "Tax" (Traditional) vs. Unified ResiliencyHUB Excellence
          </p>
        </div>

        {/* TCO Comparison Card */}
        <div className="max-w-5xl mx-auto">
          <div className="relative">
            <div className="absolute -inset-4 bg-emerald-500/10 rounded-3xl blur-3xl"></div>
            <div className="relative rounded-2xl p-4 sm:p-6 md:p-8 border border-slate-700/50 shadow-2xl overflow-x-auto" style={{ backgroundColor: '#0B111D' }}>
              
              <table className="w-full min-w-[600px]">
                <thead>
                  <tr className="border-b-2 border-slate-700">
                    <th className="text-left text-slate-300 font-semibold py-4 px-3 text-sm sm:text-base">Cost Category</th>
                    <th className="text-center text-slate-300 font-semibold py-4 px-3 text-sm sm:text-base">Traditional Legacy SaaS</th>
                    <th className="text-center text-slate-300 font-semibold py-4 px-3 text-sm sm:text-base">ResiliencyHUB</th>
                    <th className="text-center text-slate-300 font-semibold py-4 px-3 text-sm sm:text-base">Savings</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Licensing: Legacy $300k/yr × 3 = $900k | ResiliencyHUB $180k/yr × 3 = $540k | Savings $360k (40%) */}
                  <tr className="border-b border-slate-700/50 hover:bg-slate-800/30 transition-colors">
                    <td className="py-4 px-3">
                      <div className="text-white font-semibold text-sm sm:text-base mb-2">$ Licensing (End-to-End)</div>
                      <div className="text-slate-400 text-xs sm:text-sm space-y-1">
                        <div>• BIA Module: $60k/yr</div>
                        <div>• Planning Module: $80k/yr</div>
                        <div>• Crisis/Incident: $60k/yr</div>
                        <div>• Audit/Compliance: $100k/yr</div>
                      </div>
                    </td>
                    <td className="text-center py-4 px-3">
                      <div className="font-bold text-lg sm:text-xl" style={{ color: '#FF4D4D' }}>$900,000</div>
                      <div className="text-slate-500 text-xs sm:text-sm">$300k/yr × 3</div>
                    </td>
                    <td className="text-center py-4 px-3">
                      <div className="font-bold text-lg sm:text-xl" style={{ color: '#10B981' }}>$540,000</div>
                      <div className="text-slate-500 text-xs sm:text-sm">$180,000/yr × 3</div>
                    </td>
                    <td className="text-center py-4 px-3">
                      <div className="font-bold text-sm sm:text-base" style={{ color: '#10B981' }}>$360,000</div>
                      <span className="inline-block bg-emerald-500/20 text-emerald-400 text-xs font-bold px-3 py-1 rounded-lg mt-1">40% SAVED</span>
                    </td>
                  </tr>

                  {/* Implementation: Legacy $250k | ResiliencyHUB $0 | Savings $250k (100%) */}
                  <tr className="border-b border-slate-700/50 bg-slate-800/20 hover:bg-slate-800/40 transition-colors">
                    <td className="py-4 px-3">
                      <div className="text-white font-semibold text-sm sm:text-base">Implementation</div>
                      <div className="text-slate-400 text-xs sm:text-sm">One-time Setup & Config</div>
                    </td>
                    <td className="text-center py-4 px-3">
                      <div className="font-bold text-lg sm:text-xl" style={{ color: '#FF4D4D' }}>$250,000</div>
                      <div className="text-slate-500 text-xs sm:text-sm">Professional Services</div>
                    </td>
                    <td className="text-center py-4 px-3">
                      <div className="font-bold text-lg sm:text-xl" style={{ color: '#10B981' }}>$0</div>
                      <div className="text-slate-500 text-xs sm:text-sm">Self-Service</div>
                      <div className="text-slate-500 text-xs sm:text-sm">Guided-AI</div>
                    </td>
                    <td className="text-center py-4 px-3">
                      <div className="font-bold text-sm sm:text-base" style={{ color: '#10B981' }}>$250,000</div>
                      <span className="inline-block bg-emerald-500/20 text-emerald-400 text-xs font-bold px-3 py-1 rounded-lg mt-1">100% SAVED</span>
                    </td>
                  </tr>

                  {/* Staffing: Legacy $450k (1.5 FTE × 3) | ResiliencyHUB $30k (0.1 FTE × 3) | Savings $420k (93%) */}
                  <tr className="border-b border-slate-700/50 hover:bg-slate-800/30 transition-colors">
                    <td className="py-4 px-3">
                      <div className="text-white font-semibold text-sm sm:text-base">Staffing</div>
                      <div className="text-slate-400 text-xs sm:text-sm">FTEs required for maintenance</div>
                    </td>
                    <td className="text-center py-4 px-3">
                      <div className="font-bold text-lg sm:text-xl" style={{ color: '#FF4D4D' }}>$450,000</div>
                      <div className="text-slate-500 text-xs sm:text-sm">1.5 FTE × 3 yrs</div>
                    </td>
                    <td className="text-center py-4 px-3">
                      <div className="font-bold text-lg sm:text-xl" style={{ color: '#10B981' }}>$30,000</div>
                      <div className="text-slate-500 text-xs sm:text-sm">0.1 FTE × 3 yrs</div>
                    </td>
                    <td className="text-center py-4 px-3">
                      <div className="font-bold text-sm sm:text-base" style={{ color: '#10B981' }}>$420,000</div>
                      <span className="inline-block bg-emerald-500/20 text-emerald-400 text-xs font-bold px-3 py-1 rounded-lg mt-1">93% SAVED</span>
                    </td>
                  </tr>

                  {/* Consulting: Legacy $150k | ResiliencyHUB $0 | Savings $150k (100%) */}
                  <tr className="border-b border-slate-700/50 bg-slate-800/20 hover:bg-slate-800/40 transition-colors">
                    <td className="py-4 px-3">
                      <div className="text-white font-semibold text-sm sm:text-base">Consulting & Audit</div>
                      <div className="text-slate-400 text-xs sm:text-sm">Readiness & Verification</div>
                    </td>
                    <td className="text-center py-4 px-3">
                      <div className="font-bold text-lg sm:text-xl" style={{ color: '#FF4D4D' }}>$150,000</div>
                      <div className="text-slate-500 text-xs sm:text-sm">Manual Prep</div>
                    </td>
                    <td className="text-center py-4 px-3">
                      <div className="font-bold text-lg sm:text-xl" style={{ color: '#10B981' }}>$0</div>
                      <div className="text-slate-500 text-xs sm:text-sm">Real-Time Audit Ready</div>
                    </td>
                    <td className="text-center py-4 px-3">
                      <div className="font-bold text-sm sm:text-base" style={{ color: '#10B981' }}>$150,000</div>
                      <span className="inline-block bg-emerald-500/20 text-emerald-400 text-xs font-bold px-3 py-1 rounded-lg mt-1">100% SAVED</span>
                    </td>
                  </tr>

                  {/* TOTAL: Legacy $1,750,000 | ResiliencyHUB $570,000 | 67% */}
                  <tr className="border-t-4 border-slate-600">
                    <td className="py-5 px-3">
                      <div className="text-white font-bold text-base sm:text-xl">TOTAL 3-YEAR TCO</div>
                    </td>
                    <td className="text-center py-5 px-3">
                      <div className="font-bold text-xl sm:text-2xl" style={{ color: '#FF4D4D' }}>$1,750,000</div>
                    </td>
                    <td className="text-center py-5 px-3">
                      <div className="font-bold text-xl sm:text-2xl" style={{ color: '#10B981' }}>$570,000</div>
                    </td>
                    <td className="text-center py-5 px-3">
                      <span className="inline-block text-white text-sm sm:text-base font-bold px-4 py-2 rounded-lg shadow-lg" style={{ backgroundColor: '#10B981', boxShadow: '0 10px 25px -5px rgba(16, 185, 129, 0.3)' }}>67% TOTAL SAVINGS</span>
                    </td>
                  </tr>
                </tbody>
              </table>
              
              {/* Savings Banner: $1,750,000 - $570,000 = $1,180,000 */}
              <div className="mt-6 sm:mt-8 bg-gradient-to-r from-emerald-600 to-emerald-500 rounded-xl p-4 sm:p-5 text-center">
                <p className="text-emerald-100 text-sm mb-1">Your 3-Year Realized Savings</p>
                <p className="text-white font-bold text-2xl sm:text-3xl">$1,180,000</p>
              </div>
              
              {/* Tagline */}
              <p className="text-center mt-5 text-slate-400 text-sm sm:text-base italic">
                Legacy tools cost you time and headcount. <span className="font-semibold" style={{ color: '#10B981' }}>ResiliencyHUB delivers ROI from Day 1.</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
